import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Breadcrumbs from '../Components/Breadcrumbs';
import Table from '../Components/Tables';

import { useAlert } from '../Components/AlertContext';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import { VoiceProfileApiProvider, useVoiceProfileApi } from '../Services/VoiceProfileApiProvider';
import type { VoiceProfileListItemApi } from '../Services/VoiceProfileApiProviderTypes';
import { getTablePaginationFromStorage } from '../utils/tablePaginationStorage';
import { formatTableTitle } from '../utils/formatTitle';
import Button from '../Components/Buttons';
import { voiceProfileColumns } from './VoiceProfileHelpers';

function VoiceProfilesContent() {
  const navigate = useNavigate();
  const { organizationId } = useParams();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();
  const { getVoiceProfiles, deleteVoiceProfile } = useVoiceProfileApi();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [voiceProfiles, setVoiceProfiles] = useState<VoiceProfileListItemApi[]>([]);
  const [totalVoiceProfiles, setTotalVoiceProfiles] = useState(0);

  const tableTitle = 'Voice Profiles';
  const { persistedPageSize, persistedSkip } = getTablePaginationFromStorage(
    formatTableTitle(tableTitle),
  );

  const fetchVoiceProfiles = async (skip: number, limit: number) => {
    setLoading(true);
    setError(null);

    try {
      const response = await getVoiceProfiles(skip, limit);
      setVoiceProfiles(response.items);
      setTotalVoiceProfiles(response.totalCount);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to fetch voice profiles!');
    } finally {
      setLoading(false);
    }
  };

  const onDelete = (selectedIds: (string | number)[]) => {
    const selectedId = selectedIds[0];

    if (!selectedId) {
      return;
    }

    showConfirmation('Are you sure you want to delete the selected item?', async () => {
      try {
        await deleteVoiceProfile(String(selectedId));
        showAlert('success', 'Item was successfully deleted!');
        fetchVoiceProfiles(persistedSkip, persistedPageSize);
      } catch {
        showAlert('fail', 'Failed to delete the item!');
      }
    });
  };

  const onOpen = (row: VoiceProfileListItemApi) => {
    navigate(`/organizations/${organizationId}/voice-profiles/${row.key}`, {
      state: { organizationId },
    });
  };

  useEffect(() => {
    fetchVoiceProfiles(persistedSkip, persistedPageSize);
  }, []);

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-12">
          {error ? (
            <div>Error: {error}</div>
          ) : (
            <Table
              title={tableTitle}
              columns={voiceProfileColumns}
              data={voiceProfiles}
              selectableRows
              selectableRowsHighlight
              progressPending={loading}
              paginationPerPage={persistedPageSize}
              paginationTotalRows={totalVoiceProfiles}
              onRowClicked={onOpen}
              sortIcon={<i className="fa-solid fa-chevron-down"></i>}
              onDelete={onDelete}
              onChangePage={fetchVoiceProfiles}
            />
          )}
          <Button
            background="color2"
            size="large"
            onClick={() => navigate(`/organizations/${organizationId}/voice-profiles/new`)}
          >
            Create New Voice Profile
          </Button>
        </div>
      </div>
    </div>
  );
}

function VoiceProfiles() {
  return (
    <ConfirmationProvider>
      <VoiceProfileApiProvider>
        <VoiceProfilesContent />
      </VoiceProfileApiProvider>
    </ConfirmationProvider>
  );
}

export default VoiceProfiles;
